import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, AlertCircle } from 'lucide-react';
import type { Asset } from '@/types/asset';
import type { AssetSelectionFilter } from '@/types/depreciation';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { getAssetPrimaryPhotoUrl } from '@/lib/asset-utils';

// Stable empty array to prevent infinite re-render loops when used as default prop
const EMPTY_STRING_ARRAY: string[] = [];

// Static skeleton row identifiers to avoid using array index as React key
const SKELETON_ROW_IDS = ['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4', 'skeleton-5'] as const;

interface AssetSelectionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: (selectedAssets: Asset[]) => void;
  readonly title?: string;
  readonly description?: string;
  readonly excludeAssetIds?: string[]; // Asset IDs to exclude from selection (already in detail grid)
  readonly initialSelectedAssetIds?: string[]; // Asset IDs that are already selected (for editing)
  readonly confirmButtonLabel?: string; // Custom label for the confirm button
}

export function AssetSelectionDialog({ 
  open, 
  onOpenChange, 
  onConfirm,
  title = "Select Assets for Depreciation",
  description = "Choose one or more assets, or select all. You can refine the set later.",
  excludeAssetIds = EMPTY_STRING_ARRAY,
  initialSelectedAssetIds = EMPTY_STRING_ARRAY,
  confirmButtonLabel = "Add to Run"
}: AssetSelectionDialogProps): React.ReactElement {
  const { mode } = useMode();
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set(initialSelectedAssetIds));
  const [filters, setFilters] = useState<AssetSelectionFilter>({
    status: ['inService'],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableSubCategories, setAvailableSubCategories] = useState<string[]>([]);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [availableSubLocations, setAvailableSubLocations] = useState<string[]>([]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load available categories and locations when dialog opens
  useEffect(() => {
    if (open) {
      loadFilterOptions();
      // Reset selected assets to initial values when dialog opens
      setSelectedAssetIds(new Set(initialSelectedAssetIds));
    }
  }, [open, initialSelectedAssetIds, mode]);

  // Fetch assets from data service
  useEffect(() => {
    if (open) {
      loadAssets();
    }
  }, [open, filters, debouncedSearchTerm, mode]);

  const loadFilterOptions = async () => {
    try {
      setAvailableCategories([]);
      setAvailableSubCategories([]);
      setAvailableLocations([]);
      setAvailableSubLocations([]);
      
      let response: { data: Asset[] };
      
      if (mode === 'demo') {
        // In demo mode, use demo data directly
        response = dataService.getDemoAssets({ page: 1, pageSize: 10000 });
      } else {
        // In live mode, call API only - fall back to demo on error
        try {
          response = await dataService.getAssetsApiOnly({ page: 1, pageSize: 10000 });
        } catch (apiError) {
          console.warn('Assets API failed, falling back to demo:', apiError);
          response = dataService.getDemoAssets({ page: 1, pageSize: 10000 });
        }
      }
      
      // Extract unique categories, subcategories, locations, and sublocations
      // Use type guard to properly narrow the type from (string | undefined)[] to string[]
      const isString = (value: string | undefined): value is string => Boolean(value);
      const categories = Array.from(new Set(response.data.map((asset: Asset) => asset.category).filter(isString)));
      const subCategories = Array.from(new Set(response.data.map((asset: Asset) => asset.subCategory).filter(isString)));
      const locations = Array.from(new Set(response.data.map((asset: Asset) => asset.location).filter(isString)));
      const subLocations = Array.from(new Set(response.data.map((asset: Asset) => asset.subLocation).filter(isString)));
      
      setAvailableCategories([...categories].sort((a, b) => a.localeCompare(b)));
      setAvailableSubCategories([...subCategories].sort((a, b) => a.localeCompare(b)));
      setAvailableLocations([...locations].sort((a, b) => a.localeCompare(b)));
      setAvailableSubLocations([...subLocations].sort((a, b) => a.localeCompare(b)));
    } catch (err) {
      console.error('Failed to load filter options:', err);
      // Set fallback values if loading fails
      setAvailableCategories(['Computer Equipment', 'Furniture & Fixtures', 'Vehicles', 'Office Equipment', 'Manufacturing Equipment']);
      setAvailableLocations(['London HQ', 'Manchester Office', 'Birmingham Warehouse', 'Edinburgh Branch']);
    }
  };

  const loadAssets = async () => {
    setIsLoading(true);
    setError(null);
    setAssets([]);
    try {
      let response: { data: Asset[] };
      
      if (mode === 'demo') {
        // In demo mode, use demo data directly
        response = dataService.getDemoAssets({
          status: filters.status?.[0],
          category: filters.category?.[0],
          location: filters.location?.[0],
          search: debouncedSearchTerm ?? undefined,
          page: 1,
          pageSize: 10000,
        });
      } else {
        // In live mode, call API only - fall back to demo on error
        try {
          response = await dataService.getAssetsApiOnly({
            status: filters.status?.[0],
            category: filters.category?.[0],
            location: filters.location?.[0],
            search: debouncedSearchTerm ?? undefined,
            page: 1,
            pageSize: 10000,
          });
        } catch (apiError) {
          console.warn('Assets API failed, falling back to demo:', apiError);
          response = dataService.getDemoAssets({
            status: filters.status?.[0],
            category: filters.category?.[0],
            location: filters.location?.[0],
            search: debouncedSearchTerm ?? undefined,
            page: 1,
            pageSize: 10000,
          });
        }
      }
      setAssets(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
      setAssets([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out assets that are already in the detail grid, and apply client-side filters
  const filteredAssets = assets.filter((asset: any) => {
    if (excludeAssetIds.includes(asset.id)) return false;
    
    // Apply subCategory filter
    if (filters.subCategory?.[0] && asset.subCategory !== filters.subCategory[0]) return false;
    
    // Apply subLocation filter
    if (filters.subLocation?.[0] && asset.subLocation !== filters.subLocation[0]) return false;
    
    return true;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAssetIds(new Set(filteredAssets.map((a) => a.id)));
    } else {
      setSelectedAssetIds(new Set());
    }
  };

  const handleSelectAsset = (assetId: string, checked: boolean) => {
    const newSet = new Set(selectedAssetIds);
    if (checked) {
      newSet.add(assetId);
    } else {
      newSet.delete(assetId);
    }
    setSelectedAssetIds(newSet);
  };

  const handleConfirm = () => {
    const selected = assets.filter((a) => selectedAssetIds.has(a.id));
    onConfirm(selected);
    // Reset state
    setSelectedAssetIds(new Set());
    setSearchTerm('');
  };

  const allSelected = filteredAssets.length > 0 && filteredAssets.every((a) => selectedAssetIds.has(a.id));
  const canProceed = selectedAssetIds.size > 0;

  // Helper function to get badge variant based on asset status
  const getBadgeVariant = (status: string): 'default' | 'secondary' | 'outline' => {
    if (status === 'inService') return 'default';
    if (status === 'underMaintenance') return 'secondary';
    return 'outline';
  };

  // Render asset list content based on loading/empty/data states
  const renderAssetListContent = () => {
    if (isLoading) {
      return (
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
            <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
              <TableHead className="h-12 font-semibold w-[40px] pl-6"></TableHead>
              <TableHead className="h-12 font-semibold w-[60px]">Image</TableHead>
              <TableHead className="h-12 font-semibold w-[120px]">Code</TableHead>
              <TableHead className="h-12 font-semibold min-w-[200px]">Name</TableHead>
              <TableHead className="h-12 font-semibold w-[150px]">Category</TableHead>
              <TableHead className="h-12 font-semibold w-[150px]">Sub Category</TableHead>
              <TableHead className="h-12 font-semibold w-[150px]">Location</TableHead>
              <TableHead className="h-12 font-semibold w-[150px]">Sub Location</TableHead>
              <TableHead className="h-12 font-semibold text-right w-[140px]">Opening NBV</TableHead>
              <TableHead className="h-12 font-semibold w-[120px] pr-6">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Loading skeleton - show 5 placeholder rows */}
            {SKELETON_ROW_IDS.map((skeletonId, index) => (
              <TableRow 
                key={skeletonId} 
                className={`
                  ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                  animate-pulse border-b border-slate-100 dark:border-slate-800
                `}
              >
                <TableCell className="pl-6"><div className="w-4 h-4 bg-muted rounded" /></TableCell>
                <TableCell><div className="w-10 h-10 bg-muted rounded" /></TableCell>
                <TableCell><div className="h-4 bg-muted rounded w-20" /></TableCell>
                <TableCell><div className="h-4 bg-muted rounded w-32" /></TableCell>
                <TableCell><div className="h-4 bg-muted rounded w-24" /></TableCell>
                <TableCell><div className="h-4 bg-muted rounded w-20" /></TableCell>
                <TableCell><div className="h-4 bg-muted rounded w-24" /></TableCell>
                <TableCell><div className="h-4 bg-muted rounded w-20" /></TableCell>
                <TableCell><div className="h-4 bg-muted rounded w-20 ml-auto" /></TableCell>
                <TableCell className="pr-6"><div className="h-6 bg-muted rounded w-16" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    if (filteredAssets.length === 0) {
      return (
        <div className="flex items-center justify-center h-full min-h-[400px] text-muted-foreground">
          No assets match the current filters
        </div>
      );
    }

    return (
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
          <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <TableHead className="h-12 font-semibold w-[40px] pl-6"></TableHead>
            <TableHead className="h-12 font-semibold w-[60px]">Image</TableHead>
            <TableHead className="h-12 font-semibold w-[120px]">Code</TableHead>
            <TableHead className="h-12 font-semibold min-w-[200px]">Name</TableHead>
            <TableHead className="h-12 font-semibold w-[150px]">Category</TableHead>
            <TableHead className="h-12 font-semibold w-[150px]">Sub Category</TableHead>
            <TableHead className="h-12 font-semibold w-[150px]">Location</TableHead>
            <TableHead className="h-12 font-semibold w-[150px]">Sub Location</TableHead>
            <TableHead className="h-12 font-semibold text-right w-[140px]">Opening NBV</TableHead>
            <TableHead className="h-12 font-semibold w-[120px] pr-6">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredAssets.map((asset, index) => (
            <TableRow
              key={asset.id}
              className={`
                ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
              `}
            >
              <TableCell className="pl-6">
                <Checkbox
                  checked={selectedAssetIds.has(asset.id)}
                  onCheckedChange={(checked) => handleSelectAsset(asset.id, checked as boolean)}
                  aria-label={`Select ${asset.code}`}
                />
              </TableCell>
              <TableCell>
                {getAssetPrimaryPhotoUrl(asset) ? (
                  <img
                    src={getAssetPrimaryPhotoUrl(asset)}
                    alt={asset.name}
                    className="w-10 h-10 object-cover rounded border"
                  />
                ) : (
                  <div className="w-10 h-10 bg-muted rounded border flex items-center justify-center">
                    <span className="text-[10px] text-muted-foreground">No img</span>
                  </div>
                )}
              </TableCell>
              <TableCell className="font-medium">{asset.code}</TableCell>
              <TableCell>{asset.name}</TableCell>
              <TableCell>{asset.category}</TableCell>
                            <TableCell className="text-muted-foreground">{asset.subCategory ?? '-'}</TableCell>
                            <TableCell>{asset.location}</TableCell>
                            <TableCell className="text-muted-foreground">{asset.subLocation ?? '-'}</TableCell>
              <TableCell className="text-right">£{asset.netBookValue.toFixed(2)}</TableCell>
              <TableCell className="pr-6">
                <Badge variant={getBadgeVariant(asset.status)}>
                  {asset.status}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Asset ID, Name, Serial..."
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={filters.category?.[0] ?? 'all'}
                onValueChange={(value: string) =>
                  setFilters({ ...filters, category: value === 'all' ? undefined : [value] })
                }
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {availableCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subCategory">Sub Category</Label>
              <Select
                value={filters.subCategory?.[0] ?? 'all'}
                onValueChange={(value: string) =>
                  setFilters({ ...filters, subCategory: value === 'all' ? undefined : [value] })
                }
              >
                <SelectTrigger id="subCategory">
                  <SelectValue placeholder="All sub categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sub categories</SelectItem>
                  {availableSubCategories.map((subCategory) => (
                    <SelectItem key={subCategory} value={subCategory}>
                      {subCategory}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={filters.location?.[0] ?? 'all'}
                onValueChange={(value: string) =>
                  setFilters({ ...filters, location: value === 'all' ? undefined : [value] })
                }
              >
                <SelectTrigger id="location">
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {availableLocations.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subLocation">Sub Location</Label>
              <Select
                value={filters.subLocation?.[0] ?? 'all'}
                onValueChange={(value: string) =>
                  setFilters({ ...filters, subLocation: value === 'all' ? undefined : [value] })
                }
              >
                <SelectTrigger id="subLocation">
                  <SelectValue placeholder="All sub locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sub locations</SelectItem>
                  {availableSubLocations.map((subLocation) => (
                    <SelectItem key={subLocation} value={subLocation}>
                      {subLocation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selection Info */}
          <div className="flex items-center justify-between py-2 border-y">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="cursor-pointer">
                  Select all ({filteredAssets.length})
                </Label>
              </div>
              <Badge variant="secondary">
                {selectedAssetIds.size} selected
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              Showing {filteredAssets.length} assets
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Asset List */}
          <div className="flex-1 overflow-auto rounded-lg min-h-[400px] border border-slate-200 dark:border-slate-700">
            {renderAssetListContent()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canProceed}>
            {confirmButtonLabel} ({selectedAssetIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
